import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import { isEmptyValue } from "../../../utils/formValueUtils";
import { getDateFormat, parseDateSafely } from "./dateValidatorUtils";

/**
 * Check if at least one patient identifier field has a value.
 * If none have values, mark all as required.
 */
export var checkPatientIdentifier = function checkPatientIdentifier(formData, patientIdentifierFields) {
  var errors = [];
  if (!patientIdentifierFields || patientIdentifierFields.length === 0) {
    return errors;
  }

  // Check if at least one identifier field has a value
  var hasAtLeastOneIdentifier = patientIdentifierFields.some(function (fieldName) {
    var value = formData[fieldName];
    return !isEmptyValue(value);
  });

  // If no identifier is provided, mark all identifier fields as required
  if (!hasAtLeastOneIdentifier) {
    patientIdentifierFields.forEach(function (fieldName) {
      var fieldValue = formData[fieldName];
      var isFieldEmpty = isEmptyValue(fieldValue);
      errors.push({
        field: fieldName,
        error: isFieldEmpty ? "At least one patient identifier must be provided" : "",
        type: "special_check",
        conditional_metadata: {
          type: "required",
          field: "patient_identifier_group",
          value: "at_least_one_required"
        }
      });
    });
  }
  return errors;
};

/**
 * Check Stage_Clinical_Or_Pathologic field requirements based on Tissue_Diagnosis and Follow_Up_Diagnostic
 * Rules:
 * 1. If Tissue_Diagnosis in ["2","3","5","9","10","11"] → Stage_Clinical_Or_Pathologic is required
 * 2. If Tissue_Diagnosis in ["4","7","8"] → Stage_Clinical_Or_Pathologic is optional
 * 3. If Follow_Up_Diagnostic = "3" (PET/CT) → Stage_Clinical_Or_Pathologic is optional (overrides rule 1)
 * 4. If Tissue_Diagnosis = "8" and Stage_Clinical_Or_Pathologic has value → must be "1" (Clinical)
 */
export var checkStageClassification = function checkStageClassification(formData, stageClassificationFields) {
  var errors = [];
  if (!stageClassificationFields || stageClassificationFields.length === 0) {
    return errors;
  }

  // The field we're checking (should be Stage_Clinical_Or_Pathologic)
  var stageFieldName = "Stage_Clinical_Or_Pathologic";
  var stageValue = formData[stageFieldName];
  var tissueDiagnosis = formData["Tissue_Diagnosis"];
  var followUpDiagnostic = formData["Follow_Up_Diagnostic"];
  var isStageEmpty = isEmptyValue(stageValue);

  // Rule 4: If Tissue_Diagnosis = "8", Stage must be "1" (Clinical) when populated
  if (String(tissueDiagnosis) === "8" && !isStageEmpty && String(stageValue) !== "1") {
    errors.push({
      field: stageFieldName,
      error: "When Tissue Diagnosis is Clinical - without histology, Stage must be Clinical (1)",
      type: "special_check",
      conditional_metadata: {
        type: "optional",
        // Field is optional, but has validation constraint
        field: "Tissue_Diagnosis",
        value: tissueDiagnosis
      }
    });
  }

  // Rule 3: If Follow_Up_Diagnostic = "3" (PET/CT), field is optional
  if (String(followUpDiagnostic) === "3") {
    return errors; // Optional, no required error
  }

  // Rule 1: If Tissue_Diagnosis in ["2","3","5","9","10","11"], field is required
  var requiredTissueDiagnosisValues = ["2", "3", "5", "9", "10", "11"];
  if (tissueDiagnosis && requiredTissueDiagnosisValues.includes(String(tissueDiagnosis))) {
    // Always add the error object to show asterisk, but only populate error message when empty
    errors.push({
      field: stageFieldName,
      error: isStageEmpty ? "Stage Clinical or Pathologic is required for this Tissue Diagnosis" : "",
      type: "special_check",
      conditional_metadata: {
        type: "required",
        // Field is required
        field: "Tissue_Diagnosis",
        value: tissueDiagnosis
      }
    });
    return errors;
  }

  // Rule 2: If Tissue_Diagnosis in ["4","7","8"], field is optional
  var optionalTissueDiagnosisValues = ["4", "7", "8"];
  if (tissueDiagnosis && optionalTissueDiagnosisValues.includes(String(tissueDiagnosis))) {
    return errors; // Optional, no required error
  }
  return errors;
};

/**
 * Check risk factors fields requirements based on age, smoking status, and smoking history
 * Rules:
 * 1. If age (Exam_Date - Date_Of_Birth) is < 50 or > 80 years
 * 2. OR Smoking_Status is "3" (Current smoker) or "9" (Unknown)
 * 3. OR Number_Of_Packs_Year_Smoking is empty or < 20 or >= 999
 * 4. OR Number_Of_Years_Since_Quit > 15
 *
 * If ANY condition is met, all risk factor fields become required
 */
export var checkRiskFactorsRequired = function checkRiskFactorsRequired(formData, riskFactorFields, fieldSchemas) {
  var errors = [];
  if (!riskFactorFields || riskFactorFields.length === 0) {
    return errors;
  }

  // Get form values
  var examDate = formData["Exam_Date"];
  var dateOfBirth = formData["Date_Of_Birth"];
  var smokingStatus = formData["Smoking_Status"];
  var packsYearSmoking = formData["Number_Of_Packs_Year_Smoking"];
  var yearsSinceQuit = formData["Number_Of_Years_Since_Quit"];
  var triggerReasons = [];

  // Condition 1: Check age (Exam_Date - Date_Of_Birth) is < 50 or > 80
  if (examDate && dateOfBirth) {
    // Parse dates using the correct format from field schema
    var examDateFormat = getDateFormat(fieldSchemas, "Exam_Date");
    var dobFormat = getDateFormat(fieldSchemas, "Date_Of_Birth");
    var examDateObj = parseDateSafely(examDate, examDateFormat);
    var birthDateObj = parseDateSafely(dateOfBirth, dobFormat);
    if (examDateObj && birthDateObj) {
      var ageInYears = (examDateObj.getTime() - birthDateObj.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
      if (ageInYears < 50 || ageInYears > 80) {
        triggerReasons.push("(Exam Date - Date Of Birth) is outside range (<50 or >80)");
      }
    }
  }

  // Condition 2: Smoking_Status is "3" (Never smoker) or "9" (Unknown)
  if (smokingStatus) {
    if (String(smokingStatus) === "3" || String(smokingStatus) === "9") {
      triggerReasons.push("Smoking status is Never smoker or Unknown if ever smoked");
    }
  }

  // Condition 3: Number_Of_Packs_Year_Smoking is empty or < 20 or >= 999
  var packsYearEmpty = isEmptyValue(packsYearSmoking);
  var packsYearNum = Number(packsYearSmoking);
  if (packsYearEmpty || !isNaN(packsYearNum) && (packsYearNum < 20 || packsYearNum >= 999)) {
    triggerReasons.push(packsYearEmpty ? "Pack-years smoking is not reported" : "Pack-years smoking is outside range (<20 or >=999)");
  }

  // Condition 4: Number_Of_Years_Since_Quit > 15
  if (!isEmptyValue(yearsSinceQuit)) {
    var yearsSinceQuitNum = Number(yearsSinceQuit);
    if (!isNaN(yearsSinceQuitNum) && yearsSinceQuitNum > 15) {
      triggerReasons.push("Years since quit smoking exceeds 15");
    }
  }

  // If any condition is met, mark specific risk factor fields as required
  if (triggerReasons.length > 0) {
    // Only these specific fields should be marked as required
    var specificRequiredFields = ["Radon_Exposure", "Occupational_Exposures", "History_Of_Cancers", "Lung_Cancer_In_First_Deg_Rel", "Lung_Cancer_Other_First_Deg_Rel", "COPD", "Pulmonary_Fibrosis", "Second_Hand_Smoke_Exposure"];

    // Only create errors for fields that exist in riskFactorFields argument
    // This prevents creating errors for hidden fields
    var fieldsToMakeRequired = specificRequiredFields.filter(function (fieldName) {
      return riskFactorFields.includes(fieldName);
    });

    // Join all reasons with semicolons for display
    var allReasonsText = triggerReasons.join("; ");
    fieldsToMakeRequired.forEach(function (fieldName) {
      var fieldValue = formData[fieldName];
      var isFieldEmpty = isEmptyValue(fieldValue);
      errors.push({
        field: fieldName,
        error: isFieldEmpty ? "Risk factor information required: ".concat(allReasonsText) : "",
        type: "special_check",
        conditional_metadata: {
          type: "required",
          field: "risk_factors_trigger",
          value: allReasonsText
        }
      });
    });
  }
  return errors;
};

/**
 * Check histology constraints based on Histology and Tissue_Diagnosis fields
 * Rules:
 * 1. If Histology = "4", Histology_Small_Cell_LC and Histology_Non_Small_Cell_LC must be blank
 * 2. If Tissue_Diagnosis in ["4","7"] and Histology is not "99" or blank, show error
 */
export var checkHistologyConstraints = function checkHistologyConstraints(formData, histologyConstraintFields) {
  var errors = [];
  if (!histologyConstraintFields || histologyConstraintFields.length === 0) {
    return errors;
  }
  var histology = formData["Histology"];
  var tissueDiagnosis = formData["Tissue_Diagnosis"];
  var histologySmallCellLC = formData["Histology_Small_Cell_LC"];
  var histologyNonSmallCellLC = formData["Histology_Non_Small_Cell_LC"];

  // Rule 1: If Histology = "4", Histology_Small_Cell_LC and Histology_Non_Small_Cell_LC must be blank
  if (String(histology) === "4") {
    // Check Histology_Small_Cell_LC
    if (!isEmptyValue(histologySmallCellLC)) {
      errors.push({
        field: "Histology_Small_Cell_LC",
        error: "This field must be blank when Histology is '4'",
        type: "special_check",
        conditional_metadata: {
          type: "should_be_blank",
          field: "Histology",
          value: histology
        }
      });
    }

    // Check Histology_Non_Small_Cell_LC
    if (!isEmptyValue(histologyNonSmallCellLC)) {
      errors.push({
        field: "Histology_Non_Small_Cell_LC",
        error: "This field must be blank when Histology is '4'",
        type: "special_check",
        conditional_metadata: {
          type: "should_be_blank",
          field: "Histology",
          value: histology
        }
      });
    }
  }

  // Rule 2: If Tissue_Diagnosis in ["4","7"] and Histology is not "99" or blank, show error
  if (tissueDiagnosis && ["4", "7"].includes(String(tissueDiagnosis))) {
    var histologyEmpty = isEmptyValue(histology);
    if (!histologyEmpty && String(histology) !== "99") {
      errors.push({
        field: "Histology",
        error: "Histology can only be 'Unknown' or blank when tissue diagnosis is 'Non-lung cancer' or 'Non-diagnostic'.",
        type: "special_check",
        conditional_metadata: {
          type: "optional",
          field: "Tissue_Diagnosis",
          value: tissueDiagnosis
        }
      });
    }
  }
  return errors;
};

/**
 * Check AJCC Edition requirements based on staging fields
 * Rules:
 * 1. If any of ["Overall_Stage", "T_Status", "N_Status", "M_Status"] have a value, AJCC_Cancer_Staging_Edition is required
 * 2. If none of the staging fields have a value and AJCC_Cancer_Staging_Edition has a value, it should be blank
 */
export var checkAjccEditionRequired = function checkAjccEditionRequired(formData, ajccEditionFields) {
  var errors = [];
  if (!ajccEditionFields || ajccEditionFields.length === 0) {
    return errors;
  }
  var stagingFields = ["Overall_Stage", "T_Status", "N_Status", "M_Status"];
  var ajccField = "AJCC_Cancer_Staging_Edition";
  var ajccValue = formData[ajccField];
  var isAjccEmpty = isEmptyValue(ajccValue);

  // Check if any staging field has a value
  var hasAnyStagingValue = stagingFields.some(function (fieldName) {
    var value = formData[fieldName];
    return !isEmptyValue(value);
  });

  // Rule 1: If any staging field has a value, AJCC is required
  if (hasAnyStagingValue) {
    errors.push({
      field: ajccField,
      error: isAjccEmpty ? "AJCC Cancer Staging Edition is required when staging information is provided" : "",
      type: "special_check",
      conditional_metadata: {
        type: "required",
        field: "staging_fields",
        value: "populated"
      }
    });
  }

  // Rule 2: If no staging field has a value but AJCC has a value, it should be blank
  if (!hasAnyStagingValue && !isAjccEmpty) {
    errors.push({
      field: ajccField,
      error: "AJCC Cancer Staging Edition should be blank when no staging information is provided",
      type: "special_check",
      conditional_metadata: {
        type: "should_be_blank",
        field: "staging_fields",
        value: "empty"
      }
    });
  }
  return errors;
};

/**
 * Check rescheduled exam date warning
 * Rules:
 * 1. Skip if Rescheduled_Exam != "Y"
 * 2. Skip if Originally_Scheduled_Exam_Date or Exam_Date is missing
 * 3. If Originally_Scheduled_Exam_Date > Exam_Date, show warning (non-blocking)
 */
export var checkRescheduledExamDateWarning = function checkRescheduledExamDateWarning(formData, rescheduledExamDateFields, fieldSchemas) {
  var errors = [];
  if (!rescheduledExamDateFields || rescheduledExamDateFields.length === 0) {
    return errors;
  }
  var rescheduledExam = formData["Rescheduled_Exam"];
  var originallyScheduledExamDate = formData["Originally_Scheduled_Exam_Date"];
  var examDate = formData["Exam_Date"];

  // Rule 1: Skip if Rescheduled_Exam != "Y"
  if (String(rescheduledExam) !== "Y") {
    return errors;
  }

  // Rule 2: Skip if either date is missing
  if (isEmptyValue(originallyScheduledExamDate) || isEmptyValue(examDate)) {
    return errors;
  }

  // Rule 3: Check if Originally_Scheduled_Exam_Date > Exam_Date
  // Parse dates using the correct format from field schema
  var originalDateFormat = getDateFormat(fieldSchemas, "Originally_Scheduled_Exam_Date");
  var examDateFormat = getDateFormat(fieldSchemas, "Exam_Date");
  var originalDate = parseDateSafely(originallyScheduledExamDate, originalDateFormat);
  var actualExamDate = parseDateSafely(examDate, examDateFormat);
  if (originalDate && actualExamDate) {
    if (originalDate > actualExamDate) {
      var origStr = originallyScheduledExamDate;
      var examStr = examDate;
      errors.push({
        field: "Originally_Scheduled_Exam_Date",
        error: "Originally scheduled exam date (".concat(origStr, ") is after the actual exam date (").concat(examStr, "). Please verify this is correct."),
        type: "special_check",
        conditional_metadata: {
          type: "optional",
          // Non-blocking warning
          field: "Rescheduled_Exam",
          value: rescheduledExam
        }
      });
    }
  }
  return errors;
};

/**
 * Evaluates all special checks based on registry tagged fields
 */
export var evaluateSpecialChecks = function evaluateSpecialChecks(formData, registryTaggedFields, fieldSchemas) {
  var errors = [];
  if (!registryTaggedFields) {
    return errors;
  }

  // Check patient identifier requirement
  if (registryTaggedFields.patient_identifier) {
    var identifierErrors = checkPatientIdentifier(formData, registryTaggedFields.patient_identifier);
    errors.push.apply(errors, _toConsumableArray(identifierErrors));
  }

  // Check stage classification requirements
  if (registryTaggedFields.stage_classification) {
    var stageErrors = checkStageClassification(formData, registryTaggedFields.stage_classification);
    errors.push.apply(errors, _toConsumableArray(stageErrors));
  }

  // Check risk factors requirements
  if (registryTaggedFields.risk_factors_required) {
    var riskFactorErrors = checkRiskFactorsRequired(formData, registryTaggedFields.risk_factors_required, fieldSchemas);
    errors.push.apply(errors, _toConsumableArray(riskFactorErrors));
  }

  // Check histology constraints
  if (registryTaggedFields.histology_constraints) {
    var histologyErrors = checkHistologyConstraints(formData, registryTaggedFields.histology_constraints);
    errors.push.apply(errors, _toConsumableArray(histologyErrors));
  }

  // Check AJCC edition requirements
  if (registryTaggedFields.ajcc_edition_required) {
    var ajccErrors = checkAjccEditionRequired(formData, registryTaggedFields.ajcc_edition_required);
    errors.push.apply(errors, _toConsumableArray(ajccErrors));
  }

  // Check rescheduled exam date warning
  if (registryTaggedFields.rescheduled_exam_date_warning) {
    var rescheduledExamErrors = checkRescheduledExamDateWarning(formData, registryTaggedFields.rescheduled_exam_date_warning, fieldSchemas);
    errors.push.apply(errors, _toConsumableArray(rescheduledExamErrors));
  }
  return errors;
};